import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { SHARES_MODAL_MODE } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { removeInvestorFromMip } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useMutation } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	GetParsedInvestorRes,
	InvestorEligibilityFilter,
	InvestorBulkActionDto,
} from '@dealstack/shared/lib/generated';
import { Table } from '@tanstack/react-table';
import { BulkActionGroup } from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types.ts';
import {
	getFinalSelectionCount,
	getTableSelectedIds,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { INVESTOR_SYNC_DOWNLOAD_MAX_COUNT } from '@dealstack/shared/lib/common/business/investor';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { InvestorActionCallbacks } from './types';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

export const useManageInvestorsTableController = () => {
	const tMipManage = useTranslationMipManage();

	const [showExportModal, setShowExportModal] = useState(false);
	const [showManageCohortsDrawer, setShowManageCohortsDrawer] = useState(false);
	const [removeInvestorDrawerOpened, setRemoveInvestorDrawerOpened] = useState(false);
	const [investorToRemove, setInvestorToRemove] = useState<{
		id: string;
		displayName: string;
	} | null>(null);

	const {
		onDownloadInvestors,
		downloadInvestorsRequest,
		disableSendInvestorInvitation,
		mipData,
		setShowNewJoinersAndAllocationsModal,
		setShowNewLeaversModal,
		setInvestorSharesModalMode,
		setIsSendInvitationModalOpened,
		setShowSendDocumentModal,
		setSingleInvestorId,
		setSelectedInvestorIdsState,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
		setEligibilityFilter,
		setIsRequestKycModalOpen,
		mantineTableDataOperations,
		mergedFilterQuery,
		setBulkActionPayload,
		refreshData,
		startInvestorsDownloadWorkflow,
		createDownloadWorkflowRequest,
	} = useContext(MIPManageInvestorsV2Context);

	const removeInvestorMutation = useMutation({
		mutationFn: removeInvestorFromMip,

		onSuccess: () => {
			showToastV2({ title: tMipManage('investors.success'), state: 'success' });
			setRemoveInvestorDrawerOpened(false);
			setInvestorToRemove(null);
			refreshData();
		},

		onError: () => {
			showToastV2({
				title: tMipManage('send_modal.toast.error.message_2'),
				state: 'error',
			});
		},
	});

	// Use refs so buildBulkActionPayload reads live values. The bulk-actions menu captures
	// the selected action object into its own state between `onSelect` and `onExecute`,
	// so a stale closure would send an old filter (e.g., missing `eligibilityFilter`
	// applied via `onSelect`).
	const mergedFilterQueryRef = useRef(mergedFilterQuery);
	const globalSearchStringRef = useRef(mantineTableDataOperations.globalSearchString);

	useEffect(() => {
		mergedFilterQueryRef.current = mergedFilterQuery;
	}, [mergedFilterQuery]);
	useEffect(() => {
		globalSearchStringRef.current = mantineTableDataOperations.globalSearchString;
	}, [mantineTableDataOperations.globalSearchString]);

	/**
	 * Builds the bulk action payload based on the current selection mode.
	 * - Selected mode (specific rows checked): sends `selectedIds`
	 * - Filtered mode (greedy/select-all): sends `excludedIds` instead of `selectedIds`
	 *
	 * Always includes the active `filterQuery` and `searchQuery` so the backend
	 * can resolve the right investors when `selectedIds` is absent.
	 */
	const buildBulkActionPayload = useCallback(
		(table: Table<GetParsedInvestorRes>): InvestorBulkActionDto => {
			const { manualPagination, greedySelecting } = getTableMeta(table);
			const isGreedyMode = manualPagination && greedySelecting;
			const tableSelectedIds = getTableSelectedIds(table);

			const base = {
				filterQuery: mergedFilterQueryRef.current,
				searchQuery: globalSearchStringRef.current || undefined,
			};

			if (isGreedyMode) {
				return {
					...base,
					excludedIds: tableSelectedIds,
				};
			}

			return { ...base, selectedIds: tableSelectedIds };
		},
		[]
	);

	// Row action callbacks — each sets the investor selection so the modal
	// operates on the correct investor. Eligibility is handled by InvestorActionCell.
	const actionCallbacks: InvestorActionCallbacks = useMemo(
		() => ({
			onSendInvite: (investorId: string) => {
				setSingleInvestorId(investorId);
				setIsSendInvitationModalOpened(true);
			},
			onSendDocument: (investorId: string) => {
				setSelectedInvestorIdsState([investorId]);
				setShowSendDocumentModal(true);
			},
			onStartAllocation: (investorId: string) => {
				setSelectedInvestorIdsState([investorId]);
				setInvestorSharesModalMode(SHARES_MODAL_MODE.ADD_ALLOCATION);
				setShowNewJoinersAndAllocationsModal(true);
			},
			onStartLeaver: (investorId: string) => {
				setSelectedInvestorIdsState([investorId]);
				setShowNewLeaversModal(true);
			},
			onDownload: (investorId: string) => {
				void onDownloadInvestors({ selectedIds: [investorId] });
			},
			onRequestKyc: (investorId: string) => {
				setSelectedInvestorIdsState([investorId]);
				setIsRequestKycModalOpen(true);
			},
			onRemoveInvestor: (investorId: string, displayName: string) => {
				setInvestorToRemove({ id: investorId, displayName });
				setRemoveInvestorDrawerOpened(true);
			},
		}),
		[
			setSingleInvestorId,
			setIsSendInvitationModalOpened,
			setSelectedInvestorIdsState,
			setShowSendDocumentModal,
			setInvestorSharesModalMode,
			setShowNewJoinersAndAllocationsModal,
			setShowNewLeaversModal,
			onDownloadInvestors,
			setIsRequestKycModalOpen,
		]
	);

	const bulkActions = useCallback(
		(_table: Table<GetParsedInvestorRes>): BulkActionGroup<GetParsedInvestorRes> => ({
			primary: [
				{
					key: 'send-invite',
					label: tMipManage('investors.actions.send_invite'),
					dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_BULK_ACTION_INVITATION,
					disabled:
						!hasEditorRole || !!mipData?.isLocked || disableSendInvestorInvitation,
					onSelect: () => setEligibilityFilter(InvestorEligibilityFilter.INVITE),
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setIsSendInvitationModalOpened(true);
					},
				},
				{
					key: 'send-document',
					label: tMipManage('investors.actions.send_document'),
					dataTestId:
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_BULK_ACTION_SEND_DOCUMENT,
					disabled: !hasEditorRole || !!mipData?.isLocked,
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setShowSendDocumentModal(true);
					},
				},
				{
					key: 'create-investment-buy',
					label: tMipManage('investors.bulk_actions.create_investment_buy'),
					disabled: !hasEditorRole || !!mipData?.isLocked,
					onSelect: () => setEligibilityFilter(InvestorEligibilityFilter.ALLOCATION),
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setInvestorSharesModalMode(SHARES_MODAL_MODE.ADD_ALLOCATION);
						setShowNewJoinersAndAllocationsModal(true);
					},
				},
				{
					key: 'create-investment-sell',
					label: tMipManage('investors.bulk_actions.create_investment_sell'),
					disabled: !hasEditorRole || !!mipData?.isLocked,
					onSelect: () => setEligibilityFilter(InvestorEligibilityFilter.LEAVER),
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setShowNewLeaversModal(true);
					},
				},
				{
					key: 'manage-cohorts',
					label: tMipManage('investors.actions.manage_cohorts'),
					disabled: !hasEditorRole || !!mipData?.isLocked,
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setShowManageCohortsDrawer(true);
					},
				},
				{
					key: 'kyc-standalone',
					label: tMipManage('investors.actions.request_kyc'),
					disabled: (!hasEditorRole && !hasKycAdminRole) || !!mipData?.isLocked,
					onExecute: (table: Table<GetParsedInvestorRes>) => {
						const payload = buildBulkActionPayload(table);
						setBulkActionPayload(payload);
						setSelectedInvestorIdsState(payload.selectedIds ?? []);
						setIsRequestKycModalOpen(true);
					},
				},
			],
			secondary: [
				{
					key: 'download',
					label: tMipManage('investors.actions.download'),
					disabled:
						!hasExportRole ||
						downloadInvestorsRequest.isPending ||
						createDownloadWorkflowRequest.isPending,
					onExecute: (table) => {
						const payload = buildBulkActionPayload(table);
						const startAsyncExport = () => {
							setShowExportModal(true);
							startInvestorsDownloadWorkflow(payload);
						};

						if (getFinalSelectionCount(table) > INVESTOR_SYNC_DOWNLOAD_MAX_COUNT) {
							startAsyncExport();
							return;
						}

						void onDownloadInvestors(payload).then(
							(downloaded) => {
								if (!downloaded) {
									startAsyncExport();
								}
							},
							// the global mutation handler has already toasted the failure
							() => undefined
						);
					},
				},
			],
		}),
		[
			tMipManage,
			mipData?.isLocked,
			disableSendInvestorInvitation,
			downloadInvestorsRequest.isPending,
			createDownloadWorkflowRequest.isPending,
			setSelectedInvestorIdsState,
			setIsSendInvitationModalOpened,
			setShowSendDocumentModal,
			onDownloadInvestors,
			startInvestorsDownloadWorkflow,
			hasEditorRole,
			hasExportRole,
			hasKycAdminRole,
			setEligibilityFilter,
			setIsRequestKycModalOpen,
			buildBulkActionPayload,
			setBulkActionPayload,
		]
	);

	const onNewJoiners = useCallback(() => {
		setInvestorSharesModalMode(SHARES_MODAL_MODE.NEW_JOINERS);
		setShowNewJoinersAndAllocationsModal(true);
	}, [setInvestorSharesModalMode, setShowNewJoinersAndAllocationsModal]);

	const onDownloadAll = useCallback(() => {
		setShowExportModal(true);
		startInvestorsDownloadWorkflow({});
	}, [startInvestorsDownloadWorkflow]);

	return {
		onNewJoiners,
		onDownloadAll,
		isNewJoinersDisabled: !hasEditorRole || !!mipData?.isLocked,
		isDownloadAllDisabled: !hasExportRole || createDownloadWorkflowRequest.isPending,
		actionCallbacks,
		isLocked: mipData?.isLocked,
		disableSendInvestorInvitation,
		bulkActions,
		showExportModal,
		setShowExportModal,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
		setEligibilityFilter,
		removeInvestorDrawerOpened,
		setRemoveInvestorDrawerOpened,
		investorToRemove,
		setInvestorToRemove,
		removeInvestorMutation,
		showManageCohortsDrawer,
		setShowManageCohortsDrawer,
	};
};
